import * as React from 'react';
import styled from '@emotion/styled';
import H7 from '@base/H7/H7';
import Avatar from '@base/Avatar/Avatar';
import { Spacings, Fonts } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import { scoreboardColorMapping as colorMapping } from '../groupsConstants';

interface ScoreboardItemsProps {
  data: any;
  type?: string;
  showAvatar?: boolean;
  showTrophy?: boolean;
  currentUserId: number;
}

interface ScoreboardItemProps {
  height: string;
  type: string;
}

interface PositionProps {
  position: number;
  bgImage: string;
  bgColor: string;
  showTrophy: boolean;
}

const Scoreboard = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ScoreboardItem = styled.div<ScoreboardItemProps>`
  height: ${({ height }) => height};
  display: flex;
  align-items: center;
  :not(:last-child) {
    margin-bottom: ${(props) =>
      props.type === 'widget' ? Spacings.SPACING_2B : Spacings.SPACING_3B};
  }
`;

const Position = styled.span<PositionProps>`
  height: ${Spacings.SPACING_6B};
  width: ${Spacings.SPACING_6B};
  border-radius: 50%;
  background-image: ${({ position, bgImage, showTrophy }) =>
    showTrophy && position <= 3 ? `url(${getImage(bgImage)})` : null};
  background-color: ${({ position, bgColor, showTrophy }) =>
    showTrophy && position <= 3 ? 'transparent' : bgColor};
  color: ${(props) => props.color};
  margin-right: ${Spacings.SPACING_3B};
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${Fonts.ULTRA_SMALL};
  font-weight: ${Fonts.BOLD};
`;

const Circle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  height: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_4B};
  border: ${Spacings.SPACING_1} solid ${(props) => props.color};
  margin-right: ${Spacings.SPACING_6};
`;

const Square = styled.span`
  width: ${Spacings.SPACING_6};
  height: ${Spacings.SPACING_6};
  border: ${Spacings.SPACING_1} solid ${(props) => props.color};
  transform: rotate(45deg);
`;

const StyH7 = styled(H7)`
  color: ${(props) => props.color};
  max-width: 20ch;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyAvatar = styled(Avatar)`
  margin-right: ${Spacings.SPACING_3B};
`;

const numberArray = ['one', 'two', 'three'];
const ScoreboardItems: React.FC<ScoreboardItemsProps> = ({
  data,
  type,
  showAvatar,
  showTrophy,
  currentUserId
}) => {
  const getKey = (user, position) => {
    const currentUserKey = user.uid === currentUserId ? 'me' : 'default';
    return position <= 3 ? position : currentUserKey;
  };

  if (data && data.length > 0)
    return (
      <Scoreboard>
        <div>
          {data.map((user, index) => {
            const { position } = user;
            const key = getKey(user, position);

            return (
              <ScoreboardItem
                type={type}
                key={user.uid}
                height={showAvatar ? Spacings.SPACING_10B : Spacings.SPACING_6B}
              >
                <Position
                  bgImage={`groups/leaderboard-rank-${numberArray[index]}.png`}
                  bgColor={colorMapping[key].positionBgColor}
                  color={colorMapping[key].positionColor}
                  position={position}
                  showTrophy={showTrophy}
                >
                  {!showTrophy || position > 3 ? position : ''}
                </Position>
                {showAvatar && (
                  <StyAvatar
                    src={user.avatar}
                    alt="leaderboard"
                    size={Spacings.SPACING_10B}
                    circular
                  />
                )}
                <StyH7 color={colorMapping[key].color}>
                  {user.uid === currentUserId ? 'Me' : user.firstName}
                </StyH7>
              </ScoreboardItem>
            );
          })}
        </div>
        <div>
          {data.map((user, index) => {
            const key = getKey(user, index + 1);
            return (
              <ScoreboardItem
                type={type}
                key={user.uid}
                height={showAvatar ? Spacings.SPACING_10B : Spacings.SPACING_6B}
              >
                <Circle color={colorMapping[key].color}>
                  <Square color={colorMapping[key].color} />
                </Circle>
                <StyH7 color={colorMapping[key].color}>
                  {user.points} {user.points === 1 ? 'pt' : 'pts'}
                </StyH7>
              </ScoreboardItem>
            );
          })}
        </div>
      </Scoreboard>
    );

  return null;
};

export default React.memo(ScoreboardItems);
